import { useState, useRef, useEffect, useCallback } from 'react';

export const useAudioRecorder = () => {
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [duration, setDuration] = useState(0);
  const [error, setError] = useState(null);
  const [wakeLockSupported] = useState('wakeLock' in navigator);
  
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const startTimeRef = useRef(null);
  const wakeLockRef = useRef(null);
  const streamRef = useRef(null);

  // Wake Lock functions to prevent screen from turning off during recording
  const requestWakeLock = async () => {
    try {
      if ('wakeLock' in navigator) {
        wakeLockRef.current = await navigator.wakeLock.request('screen');
        console.log('Wake lock acquired - screen will stay on during recording');
        
        // Listen for wake lock release
        wakeLockRef.current.addEventListener('release', () => {
          console.log('Wake lock released');
          wakeLockRef.current = null;
        });
      } else {
        console.log('Wake Lock API not supported on this device/browser');
        // On older browsers or unsupported devices, we'll just log this
        // The recording will still work, just without screen wake lock
      }
    } catch (err) {
      console.error('Failed to acquire wake lock:', err);
      // Don't throw error - recording should still work without wake lock
    }
  };

  const releaseWakeLock = async () => {
    try {
      if (wakeLockRef.current) {
        await wakeLockRef.current.release();
        wakeLockRef.current = null;
        console.log('Wake lock manually released');
      }
    } catch (err) {
      console.error('Failed to release wake lock:', err);
    }
  };

  const startRecording = useCallback(async () => {
    try {
      setError(null);
      
      // Request wake lock to prevent screen from turning off
      await requestWakeLock();
      
      // Remove strict sampleRate to prevent OverconstrainedError on older devices (iPhone 8)
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          // sampleRate: 44100 // Let the browser/device decide the best sample rate
        } 
      });
      streamRef.current = stream;
      
      // Check for supported MIME types
      let options = { mimeType: 'audio/webm;codecs=opus' };
      
      if (typeof MediaRecorder.isTypeSupported === 'function') {
        if (!MediaRecorder.isTypeSupported(options.mimeType)) {
          console.warn(`${options.mimeType} is not supported, checking fallbacks...`);
          if (MediaRecorder.isTypeSupported('audio/mp4')) {
            options = { mimeType: 'audio/mp4' };
          } else if (MediaRecorder.isTypeSupported('audio/aac')) {
            options = { mimeType: 'audio/aac' };
          } else if (MediaRecorder.isTypeSupported('audio/webm')) {
            options = { mimeType: 'audio/webm' };
          } else {
            console.warn("No preferred mimeType supported, letting browser choose default");
            options = undefined; // Let browser choose default
          }
        }
      }
      
      console.log('Starting MediaRecorder with options:', options);
      mediaRecorderRef.current = new MediaRecorder(stream, options);
      
      chunksRef.current = [];
      
      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };
      
      mediaRecorderRef.current.onstop = () => {
        // Use the actual mime type from the recorder, or fallback to default
        const mimeType = mediaRecorderRef.current.mimeType || 'audio/webm;codecs=opus';
        const audioBlob = new Blob(chunksRef.current, { type: mimeType });
        setAudioBlob(audioBlob);
        stream.getTracks().forEach(track => track.stop());
      };
      mediaRecorderRef.current.start();
      setIsRecording(true);
      setIsPaused(false);
      setDuration(0);
      startTimeRef.current = Date.now();
      console.log('Recording started, startTime:', startTimeRef.current);
      
      // Start timer
      timerRef.current = setInterval(() => {
        setDuration(prev => prev + 1);
      }, 1000);
      
    } catch (err) {
      // Release wake lock if recording fails
      await releaseWakeLock();
      
      console.error('Error starting recording:', err);
      
      // Provide more specific error messages
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        setError('Microphone permission denied. Please allow access in your browser settings.');
      } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
        setError('No microphone found. Please check your device.');
      } else if (err.name === 'NotSupportedError') {
        setError('Audio recording format not supported on this device.');
      } else if (err.name === 'OverconstrainedError') {
        setError('Microphone requirements too strict for this device.');
      } else {
        setError('Failed to access microphone: ' + (err.message || 'Unknown error'));
      }
    }
  }, []);

  const stopRecording = useCallback(async () => {
    if (mediaRecorderRef.current && (isRecording || isPaused)) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      setIsPaused(false);
      
      // Clear timer
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      
      // Release wake lock when recording stops
      await releaseWakeLock();
    }
  }, [isRecording, isPaused]);

  const pauseRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.pause();
      setIsPaused(true);
      
      // Stop the timer
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
  }, []);

  const resumeRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'paused') {
      // SAFETY CHECK: Ensure microphone is still alive
      const isStreamActive = streamRef.current && streamRef.current.getTracks().some(track => track.readyState === 'live');
      
      if (!isStreamActive) {
        console.warn("Microphone stream lost during pause. Stopping recording to save data.");
        alert("Your microphone was disconnected (likely due to switching apps). We are saving your recording now.");
        stopRecording();
        return;
      }

      mediaRecorderRef.current.resume();
      setIsPaused(false);
      
      // Restart the timer
      timerRef.current = setInterval(() => {
        setDuration(prev => prev + 1);
      }, 1000);
    }
  }, [stopRecording]);

  const resetRecording = useCallback(async () => {
    setAudioBlob(null);
    setDuration(0);
    setError(null);
    setIsPaused(false);
    startTimeRef.current = null;
    
    // Ensure wake lock is released when resetting
    await releaseWakeLock();
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      // Release wake lock on cleanup
      if (wakeLockRef.current) {
        releaseWakeLock();
      }
    };
  }, []);

  // Handle page visibility changes - reacquire wake lock if needed
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible' && isRecording && !wakeLockRef.current) {
        console.log('Page became visible during recording - reacquiring wake lock');
        await requestWakeLock();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isRecording]);

  return {
    isRecording,
    isPaused,
    audioBlob,
    duration,
    error,
    wakeLockSupported,
    startRecording,
    stopRecording,
    pauseRecording,
    resumeRecording,
    resetRecording
  };
};
